import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AppLayout } from '@/components/AppLayout';
import { linkedinApi, calendarApi } from '@/lib/linkedin-api';
import { apiClient } from '@/lib/api-client';

interface LinkedInStatus {
  status: string;
  connectedAt?: string;
}

interface CalendarStatus {
  provider: string | null;
  status: string;
}

interface ConfigState {
  linkedin: { configured: boolean; clientId: string | null };
  google: { configured: boolean; clientId: string | null };
  outlook: { configured: boolean; clientId: string | null };
}

export default function LinkedInSettingsPage() {
  const [linkedinStatus, setLinkedinStatus] = useState<LinkedInStatus | null>(null);
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(null);
  const [configs, setConfigs] = useState<ConfigState>({
    linkedin: { configured: false, clientId: null },
    google: { configured: false, clientId: null },
    outlook: { configured: false, clientId: null },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [linkedinOrgId, setLinkedinOrgId] = useState('');
  const [savingOrgId, setSavingOrgId] = useState(false);

  // Form states
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [outlookClientId, setOutlookClientId] = useState('');
  const [outlookClientSecret, setOutlookClientSecret] = useState('');
  const [outlookTenantId, setOutlookTenantId] = useState('');
  const [savingConfig, setSavingConfig] = useState('');

  // Show/hide config forms
  const [showGoogleConfig, setShowGoogleConfig] = useState(false);
  const [showOutlookConfig, setShowOutlookConfig] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [liStatus, calStatus, liConfig, calConfig, orgData] = await Promise.all([
        linkedinApi.getStatus().catch(() => ({ status: 'disconnected' })),
        calendarApi.getStatus().catch(() => ({ provider: null, status: 'disconnected' })),
        linkedinApi.getConfig().catch(() => ({ configured: false, clientId: null, redirectUri: null })),
        calendarApi.getConfig().catch(() => ({
          google: { configured: false, clientId: null },
          outlook: { configured: false, clientId: null },
        })),
        apiClient.get<any>('/organizations/me').catch(() => null),
      ]);
      setLinkedinStatus(liStatus as LinkedInStatus);
      setCalendarStatus(calStatus as CalendarStatus);
      setConfigs({
        linkedin: { configured: liConfig.configured, clientId: liConfig.clientId },
        google: calConfig.google,
        outlook: calConfig.outlook,
      });
      if (orgData?.linkedinOrgId) {
        setLinkedinOrgId(orgData.linkedinOrgId);
      }
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGoogleConfig = async () => {
    if (!googleClientId || !googleClientSecret) {
      toast.error('Preencha Client ID e Client Secret');
      return;
    }
    setSavingConfig('google');
    try {
      await calendarApi.saveGoogleConfig({ clientId: googleClientId, clientSecret: googleClientSecret });
      toast.success('Configuração Google Calendar salva');
      setShowGoogleConfig(false);
      setGoogleClientId('');
      setGoogleClientSecret('');
      await fetchAll();
    } catch {
      toast.error('Erro ao salvar configuração Google');
    } finally {
      setSavingConfig('');
    }
  };

  const handleSaveOutlookConfig = async () => {
    if (!outlookClientId || !outlookClientSecret) {
      toast.error('Preencha Client ID e Client Secret');
      return;
    }
    setSavingConfig('outlook');
    try {
      await calendarApi.saveOutlookConfig({
        clientId: outlookClientId,
        clientSecret: outlookClientSecret,
        tenantId: outlookTenantId || undefined,
      });
      toast.success('Configuração Outlook salva');
      setShowOutlookConfig(false);
      setOutlookClientId('');
      setOutlookClientSecret('');
      setOutlookTenantId('');
      await fetchAll();
    } catch {
      toast.error('Erro ao salvar configuração Outlook');
    } finally {
      setSavingConfig('');
    }
  };

  const handleLinkedInConnect = async () => {
    setIsConnecting(true);
    try {
      const resp = await linkedinApi.getAuthUrl();
      window.location.href = resp.authUrl;
    } catch {
      toast.error('Erro ao iniciar conexão com LinkedIn');
      setIsConnecting(false);
    }
  };

  const handleLinkedInDisconnect = async () => {
    try {
      await linkedinApi.disconnect();
      setLinkedinStatus({ status: 'disconnected' });
      toast.success('LinkedIn desconectado');
    } catch {
      toast.error('Erro ao desconectar LinkedIn');
    }
  };

  const handleCalendarConnect = async (provider: 'google' | 'outlook') => {
    setIsConnecting(true);
    try {
      const resp = provider === 'google'
        ? await calendarApi.getGoogleAuthUrl()
        : await calendarApi.getOutlookAuthUrl();
      window.location.href = resp.authUrl;
    } catch {
      toast.error(`Erro ao conectar ${provider === 'google' ? 'Google Calendar' : 'Outlook'}`);
      setIsConnecting(false);
    }
  };

  const handleCalendarDisconnect = async () => {
    try {
      await calendarApi.disconnect();
      setCalendarStatus({ provider: null, status: 'disconnected' });
      toast.success('Calendário desconectado');
    } catch {
      toast.error('Erro ao desconectar calendário');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge variant="success">Conectado</Badge>;
      case 'expired':
        return <Badge variant="warning">Token Expirado</Badge>;
      case 'not_configured':
        return <Badge variant="secondary">Não Configurado</Badge>;
      default:
        return <Badge variant="secondary">Desconectado</Badge>;
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Integrações</h1>
          <p className="text-muted-foreground">Gerencie suas conexões com LinkedIn e calendários</p>
        </div>

        {/* LinkedIn */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>LinkedIn</CardTitle>
                <CardDescription>Publique vagas, busque candidatos e envie InMails</CardDescription>
              </div>
              {linkedinStatus && getStatusBadge(linkedinStatus.status)}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {linkedinStatus?.status === 'connected' ? (
              <div className="space-y-3">
                <p className="text-sm text-green-600">LinkedIn conectado com sucesso.</p>
                <Button variant="outline" onClick={handleLinkedInDisconnect}>Desconectar</Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Conecte sua conta LinkedIn para publicar vagas, buscar candidatos e enviar InMails.
                  A vaga será publicada na Company Page da sua empresa no LinkedIn.
                </p>
                <Button onClick={handleLinkedInConnect} disabled={isConnecting}>
                  {isConnecting ? 'Conectando...' : 'Conectar LinkedIn'}
                </Button>
              </div>
            )}

            {/* Company Page ID */}
            <div className="border-t pt-3 mt-3">
              <label className="text-sm font-medium" htmlFor="linkedin-org-id">
                LinkedIn Company Page ID
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Encontre na URL da sua Company Page: linkedin.com/company/<strong>NÚMERO</strong>
              </p>
              <div className="flex gap-2">
                <Input
                  id="linkedin-org-id"
                  value={linkedinOrgId}
                  onChange={e => setLinkedinOrgId(e.target.value.replace(/\D/g, ''))}
                  placeholder="Ex: 12345678"
                  className="max-w-xs"
                />
                <Button
                  size="sm"
                  disabled={savingOrgId}
                  onClick={async () => {
                    setSavingOrgId(true);
                    try {
                      await apiClient.put('/organizations/me', { linkedinOrgId: linkedinOrgId || undefined });
                      toast.success('Company Page ID salvo');
                    } catch {
                      toast.error('Erro ao salvar');
                    } finally {
                      setSavingOrgId(false);
                    }
                  }}
                >
                  {savingOrgId ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
              {linkedinOrgId && (
                <p className="text-xs text-muted-foreground mt-1">
                  Vagas serão publicadas na Company Page {linkedinOrgId}
                </p>
              )}
              {!linkedinOrgId && (
                <p className="text-xs text-amber-600 mt-1">
                  Sem Company Page ID, vagas serão publicadas no perfil pessoal de quem conectou
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Google Calendar */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Google Calendar</CardTitle>
                <CardDescription>Sincronize disponibilidade e crie eventos de entrevista</CardDescription>
              </div>
              {calendarStatus?.provider === 'google'
                ? getStatusBadge(calendarStatus.status)
                : configs.google.configured
                  ? <Badge variant="secondary">Desconectado</Badge>
                  : <Badge variant="secondary">Não Configurado</Badge>
              }
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {configs.google.configured && (
              <p className="text-sm text-muted-foreground">Client ID: {configs.google.clientId}</p>
            )}

            {calendarStatus?.provider === 'google' && calendarStatus.status === 'connected' ? (
              <div className="space-y-3">
                <Button variant="outline" onClick={handleCalendarDisconnect}>Desconectar</Button>
                <Button variant="ghost" size="sm" className="ml-2" onClick={() => setShowGoogleConfig(!showGoogleConfig)}>
                  Reconfigurar
                </Button>
              </div>
            ) : !configs.google.configured ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Configure as credenciais OAuth do Google para sua organização.
                </p>
                {renderConfigForm('google')}
              </div>
            ) : (
              <div className="space-y-3">
                <Button onClick={() => handleCalendarConnect('google')} disabled={isConnecting}>
                  Conectar Google Calendar
                </Button>
                <Button variant="ghost" size="sm" className="ml-2" onClick={() => setShowGoogleConfig(!showGoogleConfig)}>
                  Reconfigurar
                </Button>
              </div>
            )}

            {showGoogleConfig && configs.google.configured && renderConfigForm('google')}
          </CardContent>
        </Card>

        {/* Outlook */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Outlook Calendar</CardTitle>
                <CardDescription>Sincronize com Microsoft Outlook / Office 365</CardDescription>
              </div>
              {calendarStatus?.provider === 'outlook'
                ? getStatusBadge(calendarStatus.status)
                : configs.outlook.configured
                  ? <Badge variant="secondary">Desconectado</Badge>
                  : <Badge variant="secondary">Não Configurado</Badge>
              }
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {configs.outlook.configured && (
              <p className="text-sm text-muted-foreground">Client ID: {configs.outlook.clientId}</p>
            )}

            {calendarStatus?.provider === 'outlook' && calendarStatus.status === 'connected' ? (
              <div className="space-y-3">
                <Button variant="outline" onClick={handleCalendarDisconnect}>Desconectar</Button>
                <Button variant="ghost" size="sm" className="ml-2" onClick={() => setShowOutlookConfig(!showOutlookConfig)}>
                  Reconfigurar
                </Button>
              </div>
            ) : !configs.outlook.configured ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Configure as credenciais OAuth do Outlook para sua organização.
                </p>
                {renderConfigForm('outlook')}
              </div>
            ) : (
              <div className="space-y-3">
                <Button onClick={() => handleCalendarConnect('outlook')} disabled={isConnecting}>
                  Conectar Outlook
                </Button>
                <Button variant="ghost" size="sm" className="ml-2" onClick={() => setShowOutlookConfig(!showOutlookConfig)}>
                  Reconfigurar
                </Button>
              </div>
            )}

            {showOutlookConfig && configs.outlook.configured && renderConfigForm('outlook')}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );

  function renderConfigForm(provider: 'google' | 'outlook') {
    const isGoogle = provider === 'google';
    const isSaving = savingConfig === provider;

    const clientId = isGoogle ? googleClientId : outlookClientId;
    const setClientId = isGoogle ? setGoogleClientId : setOutlookClientId;
    const clientSecret = isGoogle ? googleClientSecret : outlookClientSecret;
    const setClientSecret = isGoogle ? setGoogleClientSecret : setOutlookClientSecret;
    const handleSave = isGoogle ? handleSaveGoogleConfig : handleSaveOutlookConfig;

    return (
      <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
        <div>
          <label className="text-sm font-medium" htmlFor={`${provider}-client-id`}>Client ID</label>
          <input
            id={`${provider}-client-id`}
            type="text"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Cole o Client ID aqui"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          />
        </div>
        <div>
          <label className="text-sm font-medium" htmlFor={`${provider}-client-secret`}>Client Secret</label>
          <input
            id={`${provider}-client-secret`}
            type="password"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Cole o Client Secret aqui"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </div>
        {provider === 'outlook' && (
          <div>
            <label className="text-sm font-medium" htmlFor="outlook-tenant-id">Tenant ID (opcional)</label>
            <input
              id="outlook-tenant-id"
              type="text"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="common (padrão)"
              value={outlookTenantId}
              onChange={(e) => setOutlookTenantId(e.target.value)}
            />
          </div>
        )}
        <Button onClick={handleSave} disabled={isSaving} size="sm">
          {isSaving ? 'Salvando...' : 'Salvar Configuração'}
        </Button>
      </div>
    );
  }
}
